import {
  PropsWithChildren,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import clsx from "clsx";
import { useLocation } from "react-router-dom";

import { ChevronRight } from "lucide-react";

import { Tooltip } from "@/design-system/components/Tooltip/Tooltip";

import NavBar from "./NavBar";
import { MasqueradeBar } from "@/Topbar/MasqueradeBar";
import { AnnouncementBanner } from "@/Topbar/AnnouncementBanner";
import { TopBar } from "@/Topbar/TopBar";
import { PreviewBanner } from "@/preview-mode/PreviewBanner";
import {
  NavigationLayoutProvider,
  useNavigationLayout,
} from "./NavigationLayoutContext";

const COLLAPSE_BREAKPOINT = 1440;
const DRAWER_COLLAPSE_KEY = "fp_drawer_collapse";

const getInitialCollapseState = (): boolean => {
  if (typeof window === "undefined") return false;

  const stored = localStorage.getItem(DRAWER_COLLAPSE_KEY);
  if (stored !== null) {
    return stored === "true";
  }

  return window.innerWidth <= COLLAPSE_BREAKPOINT;
};

/**
 * Persistent navigation shell rendered above the router's Suspense boundary.
 * Sidebar, top bar, and banners stay mounted across page transitions while
 * only the content area swaps out.
 */
export function AuthenticatedLayout({ children }: PropsWithChildren<unknown>) {
  return (
    <NavigationLayoutProvider>
      <AuthenticatedLayoutShell>{children}</AuthenticatedLayoutShell>
    </NavigationLayoutProvider>
  );
}

function AuthenticatedLayoutShell({ children }: PropsWithChildren<unknown>) {
  const { breadcrumbs } = useNavigationLayout();
  const location = useLocation();
  const contentScrollRef = useRef<HTMLDivElement>(null);

  const [drawerCollapse, setDrawerCollapseState] = useState(
    getInitialCollapseState,
  );

  useEffect(() => {
    const stored = localStorage.getItem(DRAWER_COLLAPSE_KEY);
    if (stored !== null) {
      setDrawerCollapseState(stored === "true");
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const hasUserPreference =
        localStorage.getItem(DRAWER_COLLAPSE_KEY) !== null;
      if (!hasUserPreference) {
        setDrawerCollapseState(window.innerWidth <= COLLAPSE_BREAKPOINT);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const setDrawerCollapse = useCallback((collapsed: boolean) => {
    setDrawerCollapseState(collapsed);
    localStorage.setItem(DRAWER_COLLAPSE_KEY, String(collapsed));
  }, []);

  const isDragging = useRef(false);
  const dragStartX = useRef(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    isDragging.current = true;
    dragStartX.current = e.clientX;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, []);

  useEffect(() => {
    if (contentScrollRef.current) {
      contentScrollRef.current.scrollTop = 0;
      return;
    }
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;

      const deltaX = e.clientX - dragStartX.current;
      const threshold = 50;

      if (Math.abs(deltaX) > threshold) {
        setDrawerCollapse(deltaX < 0);
        isDragging.current = false;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      }
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [setDrawerCollapse]);

  return (
    <div className="relative flex h-dvh">
      <aside
        className={clsx(
          "group dark:bg-nav-background relative z-10 h-full flex-none bg-gray-700 transition-[width] duration-550 ease-[cubic-bezier(0.16,1,0.3,1)]",
        )}
        style={{ width: drawerCollapse ? 56 : 240 }}
      >
        <NavBar
          drawerCollapse={drawerCollapse}
          setDrawerCollapse={setDrawerCollapse}
        />
        <div
          className="absolute top-0 right-0 h-full w-1 cursor-col-resize bg-transparent transition-colors hover:bg-blue-400"
          onMouseDown={handleMouseDown}
          onClick={() => setDrawerCollapse(!drawerCollapse)}
          title={drawerCollapse ? "Expand sidebar" : "Collapse sidebar"}
        />
      </aside>

      <Tooltip content="Expand navigation" triggerProps={{ asChild: true }}>
        <button
          type="button"
          onClick={() => setDrawerCollapse(false)}
          aria-label="Expand navigation"
          aria-hidden={!drawerCollapse}
          tabIndex={drawerCollapse ? 0 : -1}
          style={{
            left: drawerCollapse ? 46 : 230,
            opacity: drawerCollapse ? 1 : 0,
          }}
          className={clsx(
            "z-nav-float fixed top-[24px] flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-md transition-[left,opacity] duration-550 ease-[cubic-bezier(0.16,1,0.3,1)] hover:border-gray-300 hover:text-gray-700 hover:shadow-lg",
            !drawerCollapse && "pointer-events-none",
          )}
        >
          <ChevronRight size={11} strokeWidth={2.5} />
        </button>
      </Tooltip>

      <main className="flex h-full flex-1 flex-col overflow-x-hidden bg-white dark:bg-gray-50">
        <AnnouncementBanner />
        <MasqueradeBar />
        <TopBar breadcrumbs={breadcrumbs} />
        <PreviewBanner />

        <div
          ref={contentScrollRef}
          className="relative z-0 min-h-0 flex-1 overflow-y-auto p-8"
        >
          {children}
        </div>
      </main>
    </div>
  );
}
